import React, { useEffect, useState } from 'react';
import { translateApiError } from 'i18n/translateApiError';
// Chakra imports
import {
  Box,
  Button,
  Heading,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
// Custom components
import DefaultAuth from 'layouts/auth/Default';
// Assets
import illustration from 'assets/img/auth/auth.png';
import { login } from 'services/auth';
import { useRole } from 'contexts/RoleContext';
import { register } from 'services/auth';
import { EMAIL_REGEX } from 'services/firebase';
import { useActiveTab } from 'contexts/ActiveTabContext';
import { useCreateUserWithRole } from 'domains/user/hooks/useCreateUserWithRole';
import SignUpInSwitch from 'domains/auth/components/sign-in-up-container/SignUpInSwitch';
import AuthForms from 'domains/auth/components/sign-in-up-container/AuthForms';
import { useToast } from '@chakra-ui/react';
import { getAuth, updateProfile } from 'firebase/auth';
import { sendVerificationEmailToUser } from 'services/firebase';
import { resendVerificationEmail } from 'services/firebase';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'i18n/TranslationProvider';
import { UnifiedAuthPanel } from 'components/layout/UnifiedPrimitives';
import {
  clearPendingSignupRolePreference,
  normalizeRolePreference,
  setPendingSignupRolePreference,
} from 'domains/auth/utils/registrationRole';

function SignIn() {
  const { t } = useTranslation();
  // Chakra color mode
  const textColor = useColorModeValue('navy.700', 'white');
  const subTextColor = useColorModeValue('gray.600', 'gray.300');
  const [show, setShow] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const handleClick = () => setShow(!show);
  const handleClickConfirmPassword = () =>
    setShowConfirmPassword(!showConfirmPassword);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const { activeTab, setActiveTab } = useActiveTab();
  const [displayName, setDisplayName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  // New error states
  const [nameError, setNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [invalidEmailError, setInvalidEmailError] = useState(false); // New state for invalid email
  const [confirmPasswordError, setConfirmPasswordError] = useState(false);
  const [passwordMatchError, setPasswordMatchError] = useState(false);
  const { selectedRole } = useRole();
  const { createUserWithRole } = useCreateUserWithRole();
  const toast = useToast();
  const navigate = useNavigate();

  // Email regex pattern imported from firebase services
  useEffect(() => {
    if (!confirmPassword) {
      setPasswordMatchError(false);
      return;
    }
    if (password !== confirmPassword) {
      setPasswordMatchError(true);
    } else {
      setPasswordMatchError(false);
    }
  }, [password, confirmPassword]);

  const resetValidationErrors = () => {
    setEmailError(false);
    setPasswordError(false);
    setConfirmPasswordError(false);
    setPasswordMatchError(false);
    setNameError(false);
    setInvalidEmailError(false);
  };

  const handleSignin = async () => {
    if (isSubmitting) return false;

    let hasError = false;
    if (!email) {
      setEmailError(true);
      hasError = true;
    }
    if (!password) {
      setPasswordError(true);
      hasError = true;
    }
    if (!hasError && !EMAIL_REGEX.test(email)) {
      setInvalidEmailError(true);
      hasError = true;
    }
    if (hasError) return false;

    setIsSubmitting(true);
    try {
      const userCredential = await login(email, password);
      console.log('Logged in user:', userCredential.user);
      window.location.href = '/';
      return true;
    } catch (err) {
      console.error(err);

      let message = t('auth.an_unexpected_error_occurred_please_try_again');
      if (err.code) {
        switch (err.code) {
          case 'auth/invalid-email':
            message = t('auth.the_email_address_is_not_valid');
            break;
          case 'auth/user-disabled':
            message = t('auth.this_account_has_been_disabled');
            break;
          case 'auth/user-not-found':
            message = t('auth.no_account_found_with_this_email');
            break;
          case 'auth/wrong-password':
            message = t('auth.incorrect_password_please_try_again');
            break;
          case 'auth/invalid-credential':
            message = t('auth.invalid_email_or_password_please_check_your_credentials_and_try_again',
            );
            break;
          case 'Firebase: Error (auth/invalid-credential).':
            message = t('auth.too_many_failed_attempts_please_try_again_later_or_reset_your_password',
            );
            break;
          case 'auth/network-request-failed':
            message = t('auth.network_error_check_your_connection_and_try_again');
            break;
          default:
            message = translateApiError(err, message);
        }
      }

      toast({
        title: t('auth.login_failed'),
        description: message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignUp = async () => {
    if (isSubmitting) return false;
    let isFirebaseUserCreated = false;

    /* ---------- basic client-side validation ---------- */
    let hasError = false;
    if (!displayName) {
      setNameError(true);
      hasError = true;
    }
    if (!email) {
      setEmailError(true);
      hasError = true;
    } else if (!EMAIL_REGEX.test(email)) {
      setInvalidEmailError(true);
      hasError = true;
    }
    if (!password) {
      setPasswordError(true);
      hasError = true;
    }
    if (!confirmPassword) {
      setConfirmPasswordError(true);
      hasError = true;
    }
    if (password !== confirmPassword) {
      setPasswordMatchError(true);
      hasError = true;
    }
    if (!selectedRole) {
      hasError = true;
    }
    if (hasError) {
      return false;
    }

    setIsSubmitting(true);
    /* ---------- create account + back-end record ---------- */
    try {
      const normalizedRole = normalizeRolePreference(selectedRole, null);
      if (!normalizedRole) {
        throw new Error('Invalid role selection');
      }
      setPendingSignupRolePreference(normalizedRole, email);

      // 1. Firebase sign-up
      const userCredential = await register(email, password);
      isFirebaseUserCreated = true;

      // 2. Validate Firebase user exists
      if (!userCredential || !userCredential.user) {
        throw new Error('Firebase registration failed - no user returned');
      }

      const firebaseUser = userCredential.user;

      // 3. Validate required fields exist
      if (!firebaseUser.uid) {
        throw new Error('Firebase user missing uid');
      }
      if (!firebaseUser.email) {
        throw new Error('Firebase user missing email');
      }

      console.log('Firebase user created:', {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: displayName,
      });

      // 4. Update Firebase user profile with displayName
      await updateProfile(firebaseUser, { displayName: displayName });

      // 5. Reload to ensure profile is updated
      await firebaseUser.reload();

      // 6. Create user row in your DB
      const idToken = await firebaseUser.getIdToken();
      await createUserWithRole(
        firebaseUser.uid,
        firebaseUser.email,
        displayName,
        normalizedRole,
        idToken,
      );

      clearPendingSignupRolePreference();

      // 5. Send verification e-mail
      await sendVerificationEmailToUser(userCredential.user);

      toast({
        title: t('auth.verification_email_sent'),
        description: t('auth.a_verification_email_has_been_sent_to_your_email_address_please_verify_your_emai',
        ),
        status: 'info',
        duration: 9000,
        isClosable: true,
      });

      // 5. Route by role
      navigate('/email-verification');
      return true;
    } catch (error) {
      if (!isFirebaseUserCreated) {
        clearPendingSignupRolePreference();
      }
      console.error('Sign-up error:', error);
      toast({
        title: t('auth.sign_up_failed'),
        description: translateApiError(error),
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrimarySubmit = async (event) => {
    event.preventDefault();
    if (activeTab === 'signin') {
      await handleSignin();
      return;
    }
    await handleSignUp();
  };

  const handleResendVerification = async () => {
    if (isResending) return;
    setIsResending(true);

    try {
      const auth = getAuth();
      if (auth.currentUser) {
        await resendVerificationEmail(auth.currentUser);
        toast({
          title: t('auth.verification_email_resent'),
          description: t('auth.please_check_your_inbox'),
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: t('auth.you_need_to_log_in_first'),
          description: t(
            'auth.please_sign_in_to_resend_the_verification_email',
          ),
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (err) {
      toast({
        title: t('auth.failed_to_resend_email'),
        description: translateApiError(
          err,
          t('auth.an_unexpected_error_occurred_please_try_again'),
        ),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsResending(false);
    }
  };

  const roleMissing = activeTab === 'signup' && !selectedRole;
  const isPrimaryDisabled = isSubmitting || roleMissing;

  return (
    <DefaultAuth illustrationBackground={illustration}>
      <Box w="100%" maxW={{ base: '100%', sm: '540px', md: '620px' }} mx="auto">
        {/* LanguageSelector moved to Default.js */}

        <UnifiedAuthPanel px={{ base: 4, sm: 6, md: 7 }} py={{ base: 5, md: 7 }}>
          <Stack spacing={{ base: 4, md: 5 }}>
            <Box>
              <Heading
                color={textColor}
                fontSize={{ base: '2xl', md: '4xl' }}
                lineHeight="1.1"
                mb={2}
              >
                {activeTab === 'signin'
                  ? t('auth.sign_in_2')
                  : t('auth.sign_up_2')}
              </Heading>
              <Text
                color={subTextColor}
                fontWeight="500"
                fontSize={{ base: 'sm', md: 'md' }}
                maxW="46ch"
              >
                {activeTab === 'signin'
                  ? t('auth.enter_your_email_and_password_to_sign_in')
                  : t(
                      'auth.enter_your_email_create_a_password_and_choose_a_role_to_register',
                    )}
              </Text>
            </Box>

            <SignUpInSwitch
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              onSwitch={() => {
                // Clear inputs and error states when switching tabs
                setEmail('');
                setPassword('');
                setConfirmPassword('');
                setDisplayName('');
                resetValidationErrors();
              }}
            />

            <Box as="form" id="auth-form-panel" onSubmit={handlePrimarySubmit}>
              <Stack spacing={{ base: 4, md: 5 }}>
                <AuthForms
                  email={email}
                  setEmail={(val) => {
                    setEmail(val);
                    setEmailError(false);
                    setInvalidEmailError(false);
                    setConfirmPasswordError(false);
                    setPasswordMatchError(false);
                  }}
                  password={password}
                  setPassword={(val) => {
                    setPassword(val);
                    setPasswordError(false);
                  }}
                  show={show}
                  handleClick={handleClick}
                  type={activeTab}
                  name={displayName}
                  setName={(val) => {
                    setDisplayName(val);
                    setNameError(false);
                  }}
                  nameError={nameError}
                  showConfirmPassword={showConfirmPassword}
                  handleClickConfirmPassword={handleClickConfirmPassword}
                  setConfirmPassword={(val) => {
                    setConfirmPassword(val);
                    setConfirmPasswordError(false);
                    setPasswordMatchError(false);
                  }}
                  confirmPasswordError={confirmPasswordError}
                  passwordMatchError={passwordMatchError}
                  emailError={emailError}
                  passwordError={passwordError}
                  invalidEmailError={invalidEmailError}
                />

                {roleMissing && (
                  <Text color="red.500" fontSize="sm" aria-live="polite">
                    {t('auth.please_select_a_valid_role')}
                  </Text>
                )}

                <Button
                  type="submit"
                  fontSize={{ base: 'sm', md: 'md' }}
                  variant="brand"
                  fontWeight="700"
                  w="100%"
                  h={{ base: '48px', md: '54px' }}
                  borderRadius="16px"
                  isLoading={isSubmitting}
                  isDisabled={isPrimaryDisabled}
                >
                  {activeTab === 'signin'
                    ? t('auth.sign_in')
                    : t('auth.create_account')}
                </Button>

                {activeTab === 'signin' && (
                  <Button
                    type="button"
                    variant="link"
                    colorScheme="blue"
                    alignSelf="flex-start"
                    fontWeight="600"
                    fontSize="sm"
                    isLoading={isResending}
                    onClick={handleResendVerification}
                  >
                    {t('auth.resend_verification_email')}
                  </Button>
                )}
              </Stack>
            </Box>
          </Stack>
        </UnifiedAuthPanel>
      </Box>
    </DefaultAuth>
  );
}

export default SignIn;
